import { useLocalSearchParams } from 'expo-router';
import { useState } from 'react';
import { StyleSheet } from 'react-native-unistyles';
import { useQuery } from 'urql';
import { v4 } from 'uuid';
import Box from '@/components/UI/Box';
import Button from '@/components/UI/Button';
import Text from '@/components/UI/Text';
import { graphql } from '@/gql/graphql';
import Avatar from '../Custom/Avatar';
import Icon from '../Unistyles/Icon';

const GetEntityFilter = graphql(/* GraphQL */ `
  query GetEntityFilter(
    $personId: String!
    $teamId: uuid!
    $eventId: uuid!
    $isPerson: Boolean!
    $isTeam: Boolean!
    $isEvent: Boolean!
  ) {
    person_by_pk(id: $personId) @include(if: $isPerson) {
      id
      title: name
      avatar_id
      color
    }
    team_by_pk(id: $teamId) @include(if: $isTeam) {
      id
      title
      avatar_id
      color: home_primary_color
    }
    event_by_pk(id: $eventId) @include(if: $isEvent) {
      id
      title
    }
  }
`);

export default function EntityFilter({
  showPerson = true,
}: {
  showPerson?: boolean;
}) {
  const [defaultUuid] = useState(() => v4());
  const { teamId, personId, eventId } = useLocalSearchParams<{
    teamId?: string;
    personId?: string;
    eventId?: string;
  }>();

  const [{ data }] = useQuery({
    query: GetEntityFilter,
    variables: {
      personId: personId || '',
      eventId: eventId || defaultUuid,
      teamId: teamId || defaultUuid,
      isPerson: !!personId && showPerson,
      isTeam: !!teamId,
      isEvent: !!eventId,
    },
  });

  const person = data?.person_by_pk;
  const team = data?.team_by_pk;
  const event = data?.event_by_pk;
  const recipient = event && team ? team : person;

  return (
    <Box
      direction="horizontal"
      p="md"
      wrap
      justify="flex-start"
      space="xs"
      align="center"
    >
      {event ? (
        <Button
          disabled
          alignSelf="flex-start"
          size="sm"
          appearance="outline"
          borderWidth={2}
          maxWidth={256}
          variant="secondary"
          style={style.disabledFilter}
        >
          <Text numberOfLines={1}>{event?.title}</Text>
        </Button>
      ) : null}
      {team ? (
        <Button
          disabled
          gap={8}
          size="sm"
          paddingHorizontal={0}
          paddingRight={16}
          appearance="outline"
          borderWidth={2}
          maxWidth={256}
          variant="secondary"
          style={style.disabledFilter}
        >
          <Avatar
            id={team?.avatar_id || ''}
            name={team?.title || ''}
            color={team?.color}
            size="sm"
          />

          <Text flexShrink={1} numberOfLines={1}>
            {team?.title}
          </Text>
        </Button>
      ) : null}

      {recipient ? (
        <>
          <Icon name="arrow-forward-outline" size="xs" />
          <Button
            disabled
            gap={8}
            size="sm"
            paddingHorizontal={0}
            paddingRight={16}
            appearance="outline"
            borderWidth={2}
            maxWidth={256}
            variant="secondary"
            style={style.disabledFilter}
          >
            <Avatar
              id={recipient?.avatar_id || ''}
              name={recipient?.title || ''}
              color={recipient?.color}
              size="sm"
            />
            <Text flexShrink={1} numberOfLines={1}>
              {person?.title}
            </Text>
          </Button>
        </>
      ) : null}
    </Box>
  );
}

const style = StyleSheet.create({
  disabledFilter: {
    opacity: 1,
  },
});
